import { z } from "zod"
import {
  PREPARATION_TOOL,
  PREPARATION_TASK_STATUS,
  preparationEvidenceSchema,
  preparationArtifactSchema,
} from "./preparation-agent"

export const AGENT_MODEL = "gpt-6-astra"
export const AGENT_REASONING = "high"
export const AGENT_MAX_COMPLETION_TOKENS = 8192
export const agentExecutionSettingsSchema = z
  .object({
    model: z.string().min(1),
    reasoning: z.literal(AGENT_REASONING),
    maxCompletionTokens: z.literal(AGENT_MAX_COMPLETION_TOKENS),
    protocolVersion: z.literal("nova-agent-api-v1"),
  })
  .strict()
export type AgentExecutionSettings = z.infer<typeof agentExecutionSettingsSchema>
export function agentExecutionSettings(model: string): AgentExecutionSettings {
  return {
    model,
    reasoning: AGENT_REASONING,
    maxCompletionTokens: AGENT_MAX_COMPLETION_TOKENS,
    protocolVersion: "nova-agent-api-v1",
  }
}
export function isCompatibleAgentExecution(value: unknown, model: string): boolean {
  const parsed = agentExecutionSettingsSchema.safeParse(value)
  return parsed.success && parsed.data.model === model
}
export const preparationStepSchema = z.object({
  argumentsHash: z.string().optional(),
  id: z.string(),
  tool: z.enum(Object.values(PREPARATION_TOOL)),
  modelUsed: z.string().optional(),
  status: z.enum(["intent", "completed", "failed"]),
  result: preparationEvidenceSchema.nullable(),
  acceptedEvidence: z
    .array(z.object({ configSku: z.string(), evidenceId: z.string(), sourceHash: z.string() }))
    .optional(),
  createdAt: z.string(),
})
export const preparationTaskSchema = z.object({
  id: z.string(),
  runId: z.number().int(),
  runSlug: z.string(),
  marketplace: z.string(),
  category: z.string(),
  configSkus: z.array(z.string()).max(1000),
  status: z.enum(Object.values(PREPARATION_TASK_STATUS)),
  createdAt: z.string(),
  updatedAt: z.string(),
  blocker: z.string().nullable(),
  messages: z.array(
    z.object({
      id: z.string(),
      role: z.enum(["user", "assistant", "tool"]),
      content: z.string(),
      createdAt: z.string(),
    })
  ),
  steps: z.array(preparationStepSchema),
  usage: z.object({
    modelCalls: z.number(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    reservedTokens: z.number(),
  }),
  limits: z.object({
    maxModelCalls: z.number(),
    maxTokens: z.number(),
    maxToolCalls: z.number(),
    maxDurationMs: z.number(),
  }),
  artifact: preparationArtifactSchema.nullable(),
  model: z.string(),
  promptVersion: z.string(),
  execution: agentExecutionSettingsSchema.optional(),
  modelReservations: z
    .array(z.object({ id: z.string(), tokens: z.number(), settled: z.boolean() }))
    .optional(),
})
